import { useState } from "react";
import GuideShell from "@/components/GuideShell";
import { Link } from "wouter";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
};

// ── Data ─────────────────────────────────────────────────────────────────────
interface QuickWin {
  num: number;
  title: string;
  when: string;
  how: string[];
  principle: string;
}

const WINS: QuickWin[] = [
  {
    num: 1,
    title: "Use Gemini Notebook as a Personal Reference Library",
    when: "Use this when you keep re-finding the same articles, PDFs, or notes.",
    how: [
      "Upload documents you want to reference, not actively work on",
      "Treat Gemini Notebook like a shelf, not a desk",
      "Ask questions only when you need recall or context",
      "Return to your thinking tool to decide",
    ],
    principle: "Not everything needs to live in your head.",
  },
  {
    num: 2,
    title: "Upload First — Then Ask Better Questions",
    when: "Use this when you're explaining a document instead of using it.",
    how: [
      "Upload the document or notes",
      "Ask what the document says before asking what it means",
      "Ask focused questions tied to a decision",
      "Ignore anything you don't need right now",
    ],
    principle: "Good questions come from shared context.",
  },
  {
    num: 3,
    title: "Summarize Long PDFs Without Losing Nuance",
    when: "Use this when a document feels important but time-consuming.",
    how: [
      "Upload the full PDF",
      "Ask for a summary by section or theme",
      "Ask what assumptions or perspectives are present",
      "Save only the insights that matter",
    ],
    principle: "Compression shouldn't erase meaning.",
  },
  {
    num: 4,
    title: "Use the Interactive Audio Overview",
    when: "Use this when you learn better by listening than reading.",
    how: [
      "Open a notebook and click 'Audio Overview' in the Studio panel",
      "Let Gemini Notebook generate a podcast-style conversation about your sources",
      "Click 'Join' to interrupt the hosts and ask questions in real time",
      "Listen while commuting, walking, or doing low-attention tasks",
      "Return to the source only if something sparks a question",
    ],
    principle: "Different formats unlock different kinds of understanding.",
  },
  {
    num: 5,
    title: "Use Gemini Notebook With Your Thinking Tool — Not Instead of It",
    when: "Use this when you're unsure which AI tool to open.",
    how: [
      "Use Gemini Notebook to hold source material and recall grounded facts",
      "Ask it for summaries, patterns, or what a document actually says",
      "Bring insights into your thinking tool for deeper reasoning and decisions",
      "Keep the roles separate: one retrieves, one reasons",
    ],
    principle: "Different tools serve different cognitive jobs.",
  },
  {
    num: 6,
    title: "Prep Workshop or Client Material Faster",
    when: "Use this when the preparation feels repetitive or heavy.",
    how: [
      "Upload past outlines, notes, or client documents",
      "Ask for common themes, recurring questions, or gaps",
      "Shape the session around what already repeats",
      "Customize only what's new or specific to this client",
    ],
    principle: "Reuse insight — not effort.",
  },
  {
    num: 7,
    title: "Use the Studio Panel to Create, Not Just Retrieve",
    when: "Use this when you want to do more than read and summarize.",
    how: [
      "Open the Studio panel on the right side of your notebook",
      "Generate a Mind Map to visualize connections across sources",
      "Create a Report: a structured briefing doc or competitive analysis",
      "Build a Slide Deck, then revise individual slides with feedback",
      "Use Cinematic Video Overview for immersive, animated deep-dives",
      "Export Slide Decks as PDF or PPTX; use Flashcards or Quiz for recall",
    ],
    principle: "Gemini Notebook can generate — not just retrieve.",
  },
  {
    num: 8,
    title: "Create a Thinking Vault — Not Scattered Notes",
    when: "Use this when your notes feel fragmented across tools.",
    how: [
      "Upload completed thinking or reference material: finished work, not drafts",
      "Avoid storing half-formed ideas: those belong in your working tool",
      "Let Gemini Notebook become the vault; keep active thinking elsewhere",
      "Add Google Docs, Sheets, or Slides as live sources: updates sync automatically",
    ],
    principle: "Not all thinking belongs in the same place.",
  },
  {
    num: 9,
    title: "Use Deep Research to Build a Source Library",
    when: "Use this when you need to go beyond what's already uploaded.",
    how: [
      "Start with a question or topic you're actively researching",
      "Use the Sources panel search: select Fast Research for a curated source list, or Deep Research for a full synthesized report",
      "Review results: Gemini Notebook adds a summary and curated links",
      "Import the strongest sources into your notebook for grounded chat",
      "Use the library for recall, not as a substitute for your own thinking",
    ],
    principle: "A strong source library is the foundation of clear thinking.",
  },
  {
    num: 10,
    title: "Use Gemini Notebook for Sense-Making, Not Speed",
    when: "Use this when pressure to move fast clouds understanding.",
    how: [
      "Ask Gemini Notebook to surface patterns across sources",
      "Review without rushing: read for what surprises you",
      "Let understanding form before you draft or decide",
      "Decide later",
    ],
    principle: "Understanding beats acceleration.",
  },
  {
    num: 11,
    title: "Use the Mind Map to Find Hidden Connections",
    when: "Use this when a topic feels complex or you're missing the big picture.",
    how: [
      "Upload two or more related documents or sources",
      "Open Studio, then click Mind Map",
      "Let Gemini Notebook generate a visual map of concepts and relationships",
      "Click any branch to open a grounded chat on that specific topic",
    ],
    principle: "Seeing the shape of information changes how you think about it.",
  },
  {
    num: 12,
    title: "Add YouTube Videos, EPUBs, and Images as Sources",
    when: "Use this when key information lives outside standard documents.",
    how: [
      "YouTube: paste any public URL: Gemini Notebook pulls the transcript automatically",
      "EPUB: upload ebooks directly as a source type",
      "Images: upload visuals: Gemini Notebook uses OCR to read and index the content",
      "Combine any source type in the same notebook for cross-format analysis",
    ],
    principle: "Format shouldn't limit what you can reference.",
  },
  {
    num: 13,
    title: "Configure Chat for Recurring Notebooks",
    when: "Use this when you return to the same notebook repeatedly.",
    how: [
      "Open Chat and click 'Configure Chat' to set a goal, voice, or role",
      "Example: 'Act as a research assistant focused on content strategy'",
      "Every response in that notebook will be framed around your goal",
      "Chat history is now saved automatically: clear it intentionally when starting fresh on a new topic",
    ],
    principle: "A tool that knows your goal works harder for you.",
  },
  {
    num: 14,
    title: "Use Notebooks Built Into Gemini",
    when: "Use this when you want your source library accessible everywhere you already work, not in a separate tab.",
    how: [
      "Notebooks now live natively inside the Gemini app: no separate connection step",
      "Open a notebook from within Gemini and keep context across sessions",
      "Set custom instructions so responses stay aligned with your goals",
      "Your curated source library is queryable from anywhere in the ecosystem",
    ],
    principle: "A well-built knowledge base compounds over time.",
  },
  {
    num: 15,
    title: "Use Reports to Turn Sources Into Deliverables",
    when: "Use this when you need a structured output, not just a summary.",
    how: [
      "Open the Studio panel and select 'Report'",
      "Choose a Suggested Format: briefing doc, competitive analysis, FAQ, or study guide",
      "Gemini Notebook drafts the full report grounded entirely in your uploaded sources",
      "Edit sections directly in the panel: ask for rewrites, additions, or cuts",
      "Export as a Google Doc or copy into your working tool",
    ],
    principle: "A report from your sources is already half the work.",
  },
  {
    num: 16,
    title: "Use Slide Decks to Propose a Presentation Narrative",
    when: "Use this when you need to shape a story before you build the slides.",
    how: [
      "Open the Studio panel and select 'Slide Deck'",
      "Gemini Notebook generates a full presentation outline grounded in your sources",
      "Click any slide to revise the title, body, or speaker notes with a prompt",
      "Use it to test the narrative before committing to a design tool",
      "Export as PPTX and open in Google Slides or PowerPoint to finish",
    ],
    principle: "The narrative comes before the design.",
  },
  {
    num: 17,
    title: "Use Data Tables to Structure Scattered Information",
    when: "Use this when information is spread across sources and hard to compare.",
    how: [
      "Open the Studio panel and select 'Data Table'",
      "Describe what you want to compare: people, products, dates, decisions",
      "Gemini Notebook pulls matching data from across your sources into a structured table",
      "Review each cell: every entry is grounded and citable",
      "Export to Google Sheets for further analysis or sharing",
    ],
    principle: "Structure reveals what summaries hide.",
  },
  {
    num: 18,
    title: "Run Code on Your Sources",
    when: "Use this when you want analysis, not just answers: a dataset needs summing, sorting, or checking.",
    how: [
      "Ask a question that requires calculation or structure, not just recall",
      "Gemini Notebook writes and runs code against your uploaded sources automatically",
      "Review the output: it's grounded in your actual files, not estimated",
      "Use it for numbers and structure; keep judgment calls in your thinking tool",
    ],
    principle: "Grounded analysis beats confident guessing.",
  },
];

const GROUPS = [
  { label: "Quick Wins 1 – 3", wins: WINS.slice(0, 3) },
  { label: "Quick Wins 4 – 6", wins: WINS.slice(3, 6) },
  { label: "Quick Wins 7 – 10", wins: WINS.slice(6, 10) },
  { label: "Quick Wins 11 – 14", wins: WINS.slice(10, 14) },
  { label: "Quick Wins 15 – 18 · What's New", wins: WINS.slice(14, 18) },
];

// ── Chevron icon ──────────────────────────────────────────────────────────────
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      style={{
        transition: "transform 0.25s ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
        color: C.muted,
      }}
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Win card ──────────────────────────────────────────────────────────────────
function WinCard({ win, defaultOpen = false }: { win: QuickWin; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${open ? C.terracottaMid : C.border}`,
        borderLeft: `3px solid ${open ? C.terracotta : C.terracottaMid}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open
          ? "0 6px 24px oklch(0.32 0.08 255 / 0.10)"
          : hovered
          ? "0 2px 10px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "1.1rem 1.4rem",
          background: open ? C.terracottaLight : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.2s ease",
        }}
      >
        <span
          style={{
            width: 32, height: 32, minWidth: 32,
            borderRadius: "50%",
            background: open ? C.terracotta : C.terracottaLight,
            color: open ? C.white : C.terracotta,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 13, fontWeight: 700,
            transition: "background 0.2s ease, color 0.2s ease",
            flexShrink: 0,
          }}
        >
          {win.num}
        </span>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 16, fontWeight: 600, color: C.espresso,
            flex: 1, lineHeight: 1.35,
          }}
        >
          {win.title}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          style={{
            padding: "1.25rem 1.4rem 1.5rem",
            borderTop: `1px solid ${C.terracottaMid}`,
            background: C.white,
          }}
        >
          <div style={{ marginBottom: "1.1rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.35rem" }}>
              When This Helps
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", lineHeight: 1.7 }}>
              {win.when}
            </p>
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.65rem" }}>
              How
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {win.how.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 20, height: 20, minWidth: 20,
                      borderRadius: "50%",
                      background: C.linen,
                      border: `1px solid ${C.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSize: 10, fontWeight: 700, color: C.muted,
                      marginTop: 2, flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderLeft: `3px solid ${C.terracotta}`,
              borderRadius: 6,
              padding: "0.75rem 1rem",
            }}
          >
            <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, display: "block", marginBottom: "0.3rem" }}>
              Smart Stack Principle
            </span>
            <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 14, color: C.espresso, fontStyle: "italic", lineHeight: 1.5 }}>
              {win.principle}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function NotebookLMQuickWinsPage() {
  return (
    <GuideShell
      context={"Gemini Notebook guide"}
      backHref={"/resources/gemini-notebook"}
      backLabel={"Back to Gemini Notebook"}
    >

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "3.5rem 1.5rem 5rem" }}>

        {/* Hero header */}
        <div style={{ textAlign: "center", marginBottom: "3rem", paddingBottom: "2.5rem", borderBottom: `1px solid ${C.border}` }}>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.terracotta,
              marginBottom: "1rem",
            }}
          >
            Your Smart Stack Quick Wins
          </p>
          <h1
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(28px, 5vw, 40px)",
              fontWeight: 700,
              color: C.espresso,
              lineHeight: 1.2,
              marginBottom: "0.75rem",
            }}
          >
            Gemini Notebook
          </h1>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 18,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            Store thinking. Release mental load.
          </p>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 20,
              padding: "0.45rem 1.1rem",
            }}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 12, fontWeight: 600,
                color: C.terracotta,
                letterSpacing: "0.04em",
              }}
            >
              Store. Recall. Make sense.
            </span>
          </div>
        </div>

        {/* Intro card */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "1.75rem 2rem",
            marginBottom: "2.75rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.3rem" }}>
            Reduce Mental Load
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem" }}>
            Keep what matters, without holding it all in your head.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.9rem" }}>
            Gemini Notebook is most useful when it acts as a reference layer, not a workspace. Upload your sources: PDFs, Google Docs, links, YouTube videos, EPUBs, images, audio files, then ask questions grounded entirely in what you've provided. It doesn't guess from the open web. It works from what you give it.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.5rem" }}>
            This guide shows how to use it to store, recall, and make sense of information, without overorganizing or overthinking.
          </p>

          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.6rem" }}>
              How to Use This Guide
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 16, fontWeight: 600, color: C.espresso, marginBottom: "0.5rem" }}>
              This is not a checklist.
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginBottom: "0.75rem" }}>
              You don't need to apply everything here. Each Quick Win is designed for moments when:
            </p>
            {[
              "information feels heavy",
              "ideas feel scattered across tools",
              "you want recall or perspective without reopening everything",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", marginBottom: "0.45rem" }}>
                <span style={{ color: C.terracotta, fontWeight: 700, fontSize: 15, flexShrink: 0, lineHeight: 1.6 }}>–</span>
                <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>{item}</p>
              </div>
            ))}
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginTop: "0.85rem" }}>
              Use the one that gives you relief. Ignore the rest.
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, fontWeight: 600, color: C.espresso, marginTop: "0.6rem", fontStyle: "italic" }}>
              Clarity comes from knowing where things belong.
            </p>
          </div>
        </div>

        {/* Quick Win groups */}
        {GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: "2.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.1rem" }}>
              <div style={{ height: 1, flex: 1, background: C.border }} />
              <p
                style={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: 10, letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: C.terracotta,
                  whiteSpace: "nowrap",
                }}
              >
                {group.label}
              </p>
              <div style={{ height: 1, flex: 1, background: C.border }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              {group.wins.map((win, i) => (
                <WinCard
                  key={win.num}
                  win={win}
                  defaultOpen={i === 0 && group.wins[0].num === 1}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Closing note */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "2rem 2.25rem",
            marginBottom: "2.5rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.5rem" }}>
            A Note on Cognitive Load
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem", lineHeight: 1.7 }}>
            Gemini Notebook works best when it holds what you don't want to carry.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.5rem" }}>
            It's not a place to think through decisions,
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.25rem" }}>
            it's a place to store, recall, and make sense of what already exists. Use it to reduce mental weight, not add structure for its own sake.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1.75rem" }}>
            {["Store what's done.", "Think where it's lightest.", "Trust your system."].map((line) => (
              <p
                key={line}
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 16, fontWeight: 600,
                  color: C.espresso,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {line}
              </p>
            ))}
          </div>
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 8,
              padding: "1.1rem 1.4rem",
            }}
          >
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, color: C.espresso, fontStyle: "italic", lineHeight: 1.65, marginBottom: "0.6rem" }}>
              "Gemini Notebook is where I put things I don't want to hold in my head."
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 700, color: C.terracotta, marginBottom: "0.1rem" }}>
              Your Smart Stack
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>
              Your Smart Stack
            </p>
          </div>
        </div>

        {/* Footer nav */}
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            href="/resources/gemini-notebook"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.muted,
              fontWeight: 600,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              background: C.white,
              transition: "border-color 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.terracotta;
              e.currentTarget.style.color = C.terracotta;
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.color = C.muted;
            }}
          >
            ← Gemini Notebook Resources
          </Link>
          <a
            href="https://notebooklm.google/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.white,
              fontWeight: 700,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              background: C.terracotta,
              borderRadius: 8,
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "0.85")
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "1")
            }
          >
            Try Gemini Notebook →
          </a>
        </div>
      </main>


      <style>{`
        @media (max-width: 600px) {
          main { padding: 2rem 1rem 4rem !important; }
        }
      `}</style>
    </GuideShell>
  );
}
